import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, message, Select, Spin, TreeSelect, Typography } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { requestform } from 'messages';
import {
  useLazyGetDirectCommandersQuery,
  useLazyGetWorkersQuery,
  usePostCreateWorkerOrderFromCrmMutation
} from 'services/request-form/construction-site-management.api';
import { CreateWorkerOrderFromCrm, DepartmentCTApp, DirectCommander, Worker } from '../../../types';
import { GetAppCTDepartments } from '../../../hooks/request-form/useContructionSiteManagementTicket';
import { LOCAL_STORAGE_KEY, safeLS } from '../../../utils';

type ModalTransferAppCTProps = {
  ticketId: number;
  cancelDetailModal: () => void;
  onActionSuccess: () => void;
};

const ModalTransferAppCT: React.FC<ModalTransferAppCTProps> = ({ ticketId, cancelDetailModal, onActionSuccess }) => {
  const { departments, isLoading: loadingDepartments } = GetAppCTDepartments();

  const [triggerCommanders, { data: commanders, isFetching: loadingCommanders }] = useLazyGetDirectCommandersQuery();
  const [triggerWorkers, { data: workers, isFetching: loadingWorkers }] = useLazyGetWorkersQuery();

  const [departmentCode, setDepartmentCode] = useState<string>();
  const [commanderCode, setCommanderCode] = useState<string>();
  const [workerCode, setWorkerCode] = useState<string>();

  const pendingCommanderRef = useRef<string | undefined>(undefined);
  const pendingWorkerRef = useRef<string | undefined>(undefined);

  const departmentsList: DepartmentCTApp[] = Array.isArray(departments)
    ? departments
    : ((departments as any)?.data ?? (departments as any)?.items ?? (departments ? [departments as any] : []));

  const treeData = useMemo(() => {
    const mapNode = (n: DepartmentCTApp): any => ({
      title: n.title,
      value: n.value,
      key: n.key,
      children: Array.isArray(n.children) ? n.children.map(mapNode) : undefined
    });
    return Array.isArray(departmentsList) ? departmentsList.map(mapNode) : [];
  }, [departmentsList]);

  const commandersList: DirectCommander[] = Array.isArray(commanders)
    ? (commanders as DirectCommander[])
    : ((commanders as any)?.data ?? (commanders as any)?.items ?? []);

  const workersList: Worker[] = Array.isArray(workers)
    ? (workers as Worker[])
    : ((workers as any)?.data ?? (workers as any)?.items ?? []);

  const commanderOptions: DefaultOptionType[] = commandersList.map((c) => {
    const emailName = c.email?.slice(0, c.email.indexOf('@')) ?? '';
    return {
      label: `${c.name} - ${emailName}`,
      value: c.userCode
    };
  });

  const workerOptions: DefaultOptionType[] = workersList.map((w) => {
    const emailName = w.email?.slice(0, w.email.indexOf('@')) ?? '';
    return {
      label: `${w.name} - ${emailName}`,
      value: w.code
    };
  });

  // (2b) Khôi phục từ localStorage khi mount
  useEffect(() => {
    const savedDept = safeLS.get(LOCAL_STORAGE_KEY.CT_DEPARTMENT) || undefined;
    const savedCmd = safeLS.get(LOCAL_STORAGE_KEY.CT_COMMANDER) || undefined;
    const savedWorker = safeLS.get(LOCAL_STORAGE_KEY.CT_WORKER) || undefined;

    if (savedDept) {
      pendingCommanderRef.current = savedCmd;
      pendingWorkerRef.current = savedWorker;
      onChangeDepartment(savedDept);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (2c) apply pending khi danh sách đã có
  useEffect(() => {
    const p = pendingCommanderRef.current;
    if (p && commandersList.some((c) => c.userCode === p)) {
      setCommanderCode(p);
      pendingCommanderRef.current = undefined;
    }
  }, [commandersList]);

  useEffect(() => {
    const p = pendingWorkerRef.current;
    if (p && workersList.some((w) => w.code === p)) {
      setWorkerCode(p);
      pendingWorkerRef.current = undefined;
    }
  }, [workersList]);

  useEffect(() => {
    safeLS.set(LOCAL_STORAGE_KEY.CT_DEPARTMENT, departmentCode);
  }, [departmentCode]);
  useEffect(() => {
    safeLS.set(LOCAL_STORAGE_KEY.CT_COMMANDER, commanderCode);
  }, [commanderCode]);
  useEffect(() => {
    safeLS.set(LOCAL_STORAGE_KEY.CT_WORKER, workerCode);
  }, [workerCode]);

  const onChangeDepartment = (value?: string) => {
    setDepartmentCode(value);
    setCommanderCode(undefined);
    setWorkerCode(undefined);

    //  Remove Local Storage whenever choose another department
    safeLS.remove(LOCAL_STORAGE_KEY.CT_COMMANDER);
    safeLS.remove(LOCAL_STORAGE_KEY.CT_WORKER);

    if (value) {
      triggerCommanders(value);
      triggerWorkers(value);
    }
  };

  //  Action transfer
  const [createWorkerOrderFromCrmAction] = usePostCreateWorkerOrderFromCrmMutation();
  const onTransfer = () => {
    if (!commanderCode) {
      message.warning('Vui lòng chọn Chỉ huy trực tiếp.');
      return;
    }
    if (!workerCode) {
      message.warning('Vui lòng chọn Công nhân.');
      return;
    }

    const selectedCommander = commandersList.find((c) => c.userCode === commanderCode);
    const selectedWorker = workersList.find((w) => w.code === workerCode);

    if (!selectedCommander || !selectedWorker) {
      message.error('Thiếu thông tin Chỉ huy hoặc Công nhân.');
      return;
    }

    const data: CreateWorkerOrderFromCrm = {
      ticketId,
      directCommanderCode: selectedCommander.userCode,
      directCommanderName: selectedCommander.name,
      directCommanderPersonalNumber: selectedCommander.personalNumber,
      workerCode: selectedWorker.code,
      workerName: selectedWorker.name,
      workerPersonalNumber: selectedWorker.personalNumber
    };
    if (!data) return;

    createWorkerOrderFromCrmAction(data)
      .unwrap()
      .then((res) => {
        // console.log(res)
        message.info(res.message);
        onActionSuccess();
        cancelDetailModal();
      })
      .catch((err) => {
        cancelDetailModal();
        message.error(err.message);
      });
  };

  return (
    <Card className='confirm-modal mx-auto w-full bg-[#F5F5F7] py-4 text-center'>
      {/* --- Card Title --- */}
      <div className='ml-6 flex justify-start'>
        <Typography.Text className='text-0.5xl font-semibold text-[#141414]'>
          {requestform.transferAppCTTitle ?? 'Chuyển App CT'}
        </Typography.Text>
      </div>

      <div className='mx-6 my-4 mb-0 space-y-4 text-left'>
        {/* Phòng ban */}
        <div>
          <Typography.Text strong>Phòng ban</Typography.Text>
          <div className='mt-2'>
            <Spin spinning={loadingDepartments}>
              <TreeSelect
                allowClear
                showSearch
                treeDefaultExpandAll
                style={{ width: '100%' }}
                placeholder='Chọn phòng ban'
                treeData={treeData}
                value={departmentCode}
                onChange={onChangeDepartment}
                filterTreeNode={(input, node) => (node?.title as string)?.toLowerCase().includes(input.toLowerCase())}
              />
            </Spin>
          </div>
        </div>

        {/* Chỉ huy trực tiếp */}
        <div>
          <Typography.Text strong>Chỉ huy trực tiếp</Typography.Text>
          <div className='mt-2'>
            <Select
              allowClear
              showSearch
              placeholder={departmentCode ? 'Chọn chỉ huy trực tiếp' : 'Chọn phòng ban trước'}
              options={commanderOptions}
              value={commanderCode}
              onChange={setCommanderCode}
              style={{ width: '100%' }}
              disabled={!departmentCode}
              loading={loadingCommanders}
              filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            />
          </div>
        </div>

        {/* Công nhân */}
        <div>
          <Typography.Text strong>Công nhân</Typography.Text>
          <div className='mt-2'>
            <Select
              allowClear
              showSearch
              placeholder={departmentCode ? 'Chọn công nhân' : 'Chọn phòng ban trước'}
              options={workerOptions}
              value={workerCode}
              onChange={setWorkerCode}
              style={{ width: '100%' }}
              disabled={!departmentCode}
              loading={loadingWorkers}
              filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            />
          </div>
        </div>

        {/* Nút hành động */}
        <div className={'flex justify-end'}>
          <Button
            type='primary'
            onClick={onTransfer}
            // disabled={!selectedUnitId || !requestContent.trim()}
          >
            Xác nhận
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ModalTransferAppCT;
